import React, { useEffect, useState } from 'react'
import { Drawer, Tabs, Dropdown, Menu, DatePicker } from 'antd'
import { Link, Route, Switch, useHistory, useParams } from 'react-router-dom'
import pluralize from 'pluralize' 

import { AiOutlineDown, AiOutlineEdit } from 'react-icons/ai'

import { useToggle, timeDiff, usePet, stringifyDate, useListData, patientName, server } from '../../modules'
import { useSelector } from 'react-redux'

import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'
import SendFor from '../reusable/sendFor'
import Timeline from '../reusable/timeline'
import ClaimStatusCard from '../reusable/claimStatusCard'
import PreauthStatusCard from '../reusable/preauthStatusCard'
import ConsentOtp from '../modals/consentOtp'

import PatientHistory from './patientHistory'
import NotesDrawer from './notes'
import AdmDocument from './admDocument'
import Medication from './medication'

import images from '../../images'
import RequestItems from './requestItems'
import Surgery from './surgery'
import AdjustDates from '../modals/adjustDate'
import MovePatient from '../modals/movePatient'

const { TabPane } = Tabs
const { RangePicker } = DatePicker

const MedHistory = ({ admission }) => {
    let group = 'Medication'
    const [ranges, setRanges] = useState()
    const [string, setString] = useState()

    const [includeOthers, toggleOthers] = useToggle()

    const { user } = useSelector(state => state.application)

    const [
        { loading: itLoading, error: itError, fetch: fetchBillItems },
        { list: billItems },
        { page: itPage = 1, limit: itLimit = 10, navigate, Paginate }
    ] = useListData('billedItems', ranges ? {
        patient: admission?.patient?._id,
        attachedTo: includeOthers ? undefined : admission?._id,
        string,
        group,
        from: ranges[0]?._d ?? null,
        to: ranges[1]?._d ?? null
    } : { string, group, attachedTo: includeOthers ? undefined : admission?._id, patient: admission?.patient?._id }, { notify: true }, 'items')

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    const searchSent = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            refresh()
        }
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const selectOthers = () => {
        toggleOthers()
        refresh()
    }

    return (
        <>
            <div className="sm:flex sm:justify-between sm:items-center mb-2">
                <span>
                    <input
                        onChange={e => searchSent(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-ssearch"
                        type="text"
                        placeholder="Search for item" />
                    <span>
                        <input
                            onChange={ selectOthers }
                            checked={ includeOthers }
                            type="checkbox"
                            className="form-checkbox cursor-pointer h-5 w-5" />
                        <span onClick={ selectOthers } className="text-gray-500 cursor-pointer text-xs italic ml-2">Show medication from outside this period</span>
                    </span>
                </span>
                {
                    includeOthers &&
                    <RangePicker onChange={selectRange} />
                }
            </div>
            <div className="">
                <Receive
                    progress
                    loading={itLoading}
                    error={itError}>
                    {
                        billItems.length > 0 ?
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Item</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Quantity</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Sent By</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            billItems.map((item, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{((itPage - 1) * itLimit) + (key + 1)}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.name} {item.quantity > 1 && ` (${item.instructions})`}</div>
                                                        <p className="text-gray-500 my-0 text-xs italic">Sent On {stringifyDate(item.date)}</p>
                                                        <p className={`text-${item.seen ? 'green' : 'yellow'}-500 mt-0 text-xs italic mb-0`}>{item.seen ? 'Seen' : 'Pending'}</p>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.quantity} { pluralize(item?.ref?.ref?.issuedIn ?? 'Units', item.quantity) }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.sentBy?.name}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={() => null}>View</Menu.Item>
                                                                    {
                                                                        !item.seen && (item.sentBy._id === user?._id || user?.issuperuser) &&
                                                                        <>
                                                                            <Menu.Item onClick={() => null}>Edit</Menu.Item>
                                                                            <Menu.Item onClick={() => null}>Delete</Menu.Item>
                                                                        </>
                                                                    }
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            :
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">No {pluralize(group.toLowerCase())} were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const History = ({ group, category, admission, name }) => {
    const [ranges, setRanges] = useState()
    const [string, setString] = useState()

    const [includeOthers, toggleOthers] = useToggle()

    const { user } = useSelector(state => state.application)

    const [
        { loading: itLoading, error: itError, fetch: fetchBillItems },
        { list: billItems },
        { page: itPage = 1, limit: itLimit = 10, navigate, Paginate }
    ] = useListData('billedItems', ranges ? {
        patient: admission?.patient?._id,
        attachedTo: includeOthers ? undefined : admission?._id,
        string,
        group,
        category,
        from: ranges[0]?._d ?? null,
        to: ranges[1]?._d ?? null
    } : { string, group, category, attachedTo: includeOthers ? undefined : admission?._id, patient: admission?.patient?._id }, { notify: true }, 'items')

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    const searchSent = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            refresh()
        }
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const selectOthers = () => {
        toggleOthers()
        refresh()
    }

    return (
        <>
            <div className="sm:flex sm:justify-between sm:items-center mb-2">
                <span>
                    <input
                        onChange={e => searchSent(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-ssearch"
                        type="text"
                        placeholder="Search for item" />
                    <span>
                        <input
                            onChange={ selectOthers }
                            checked={ includeOthers }
                            type="checkbox"
                            className="form-checkbox cursor-pointer h-5 w-5" />
                        <span onClick={ selectOthers } className="text-gray-500 cursor-pointer text-xs italic ml-2">Show { name ?? group?.toLowerCase() ?? 'history' } from outside this period</span>
                    </span>
                </span>
                {
                    includeOthers &&
                    <RangePicker onChange={selectRange} />
                }
            </div>
            <div className="">
                <Receive
                    progress
                    loading={itLoading}
                    error={itError}>
                    {
                        billItems.length > 0 ?
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Item</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Sent By</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            billItems.map((item, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{((itPage - 1) * itLimit) + (key + 1)}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.name} {item.quantity > 1 && ` (x ${item.quantity})`}</div>
                                                        <p className="text-gray-500 my-0 text-xs italic">Sent On {stringifyDate(item.date)}</p>
                                                        <p className={`text-${item.seen ? 'green' : 'yellow'}-500 mt-0 text-xs italic mb-0`}>{item.seen ? 'Seen' : 'Pending'}</p>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.sentBy?.name}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={() => null}>View</Menu.Item>
                                                                    {
                                                                        !item.seen && (item.sentBy._id === user?._id || user?.issuperuser) &&
                                                                        <>
                                                                            <Menu.Item onClick={() => null}>Edit</Menu.Item>
                                                                            <Menu.Item onClick={() => null}>Delete</Menu.Item>
                                                                        </>
                                                                    }
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            :
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">{ name === 'issued items' ? `No requests were made for ${patientName(admission?.patient?.name)}` : `${patientName(admission?.patient?.name)} wasn't sent for any ${ group?.toLowerCase() ?? category?.toLowerCase() ?? 'items' }` }</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const Notes = ({ path }) => {
    const { id } = useParams()

    return (
        <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1 lg:grid-cols-3">
            {
                Array(7).fill().map((x, i) =>
                    <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                        <div className="flex items-center justify-between border-b-2 border-gray-200 w-full pb-2">
                            <p className="font-medium text-sm mb-0">By Reinhardt Collins</p>
                            <Link to={`/app/inpatient/${path}/${id}/notes/1`}>
                                <button className="btn py-1 shadow-none bg-white hover:bg-gray-100 hover:text">Edit <AiOutlineEdit className="ml-2" /></button>
                            </Link>
                        </div>
                        {
                            i % 2 === 0 ?
                            <p className="pt-2">Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged.</p>
                            : <p className="pt-2">Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not.</p>
                        }
                        <div className="text-right border-t-2 border-gray-200 w-full py-2">
                            <p className="font-light text-xs mb-0">{ stringifyDate(new Date()) }</p>
                        </div>
                    </div>
                )
            }            
        </div>
    )
}

const Vitals = ({ path = 'admissions' }) => {
    const { id } = useParams()

    return (
        <div className="grid gap-3 sm:grid-cols-1 xl:grid-cols-2">
            {
                Array(3).fill().map(x =>
                    <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-4 pb-1">
                        <span className="flex justify-between items-center">
                            <div className="flex mb-2">
                                <div className="mr-2 text-center p-2">
                                    <p className="font-semibold text-xl mb-1">75 Kg </p>
                                    <p className="font-light text-xs mb-0">Weight</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">140 cm</p>
                                    <p className="font-light text-xs mb-0">Height</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">30 Deg</p>
                                    <p className="font-light text-xs mb-0">Temperature</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">140 / 72 mmHg</p>
                                    <p className="font-light text-xs mb-0">Blood Pressure</p>
                                </div>
                                {/* <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">25</p>
                                    <p className="font-light text-xs mb-0">Pulse</p>
                                </div> */}
                            </div>
                            <Link to={`/app/inpatient/${path}/${id}/vitals/2`}>
                                <button className="flex self-start btn mb-2 py-1 shadow-none bg-white hover:bg-gray-100 hover:text">View</button>
                            </Link>
                        </span>
                        <div className="flex justify-between border-t-2 mb-2 border-gray-200 w-full pt-2">
                            <p className="font-semibold italic text-xs mb-1">Taken by Reinhardt Collins</p>
                            <p className="font-light text-xs mb-0">{ stringifyDate(new Date()) }</p>
                        </div>
                    </div>
                )
            }
        </div>
    )
}

const Admission = ({ onClose, path = 'admissions' }) => {
    const [admission, setAdmission] = useState()
    const [saleItem, setSaleItem] = useState()
    const [attached, setAttached] = useState([])
    const [groups, setGroups] = useState([])
    const [beds, setBeds] = useState([])
    const [vitals, setVitals] = useState()
    const [notes, setNotes] = useState()
    const [tab, setTab] = useState("0")
    const [status, setStatus] = useState('Discharged')
    const [claim, setClaim] = useState()
    const [pendingDischargeStatus, setPendingDischargeStatus] = useState('Discharged')

    const history = useHistory()
    const { id } = useParams()

    const [visible, toggleVisible] = useToggle(true)
    const [iframe, toggleIframe] = useToggle(true)
    const [adjustDate, toggleAdjustDate] = useToggle()
    const [move, toggleMove] = useToggle()
    const [showDischargeOtp, , setShowDischargeOtp] = useToggle()
    
    const [{ loading, error }] = usePet('itemGroups', null, { notify: true, callback: ({ data }) => setGroups(data) })
    const [{ loading: bLoading, error: bError }, getBedHistory] = usePet('bedHistory', { id }, { notify: true, callback: ({ data }) => setBeds(data) })
    const [{ loading: vLoading, error: vError }] = usePet('singleSaleItem', { name: 'Vitals' }, { notify: true, callback: ({ data }) => setVitals(data) })
    const [{ loading: nLoading, error: nError }] = usePet('singleSaleItem', { name: 'Notes' }, { notify: true, callback: ({ data }) => setNotes(data) })
    const [{ loading: cLoading }, fetchClaim] = usePet('claimStatus', { admission: id, ensure: true }, { notify: false, callback: ({ data }) => setClaim(data), manual: true })
    
    const [{ loading: aLoading, error: aError }, refetchAdmission] = usePet('billedItem', { id }, { notify: true, callback: ({ data }) => setAdmission(data) })
    const [{ loading: attLoading, error: attError }, getAttachedDocs] = usePet('attachedDocuments', { id }, { notify: true, callback: ({ data }) => setAttached(data) })

    console.log({ attached })

    const claimConsentMethod = claim?.consentSession?.consentMethod ?? 'otp'
    const requiresFinalConsent = Boolean(claim && claim?.serviceType === 'INPATIENT')
    const hasFinalConsent = !requiresFinalConsent
        ? true
        : claimConsentMethod === 'biometric'
            ? Boolean(claim?.dischargeAuthorizationCode)
            : Boolean(claim?.dischargeOtp)
    const requiresDischargeOtp = Boolean(
        requiresFinalConsent
        && claimConsentMethod !== 'biometric'
        && !claim?.dischargeOtp,
    )
    const requiresDischargeBiometric = Boolean(
        requiresFinalConsent
        && claimConsentMethod === 'biometric'
        && !claim?.dischargeAuthorizationCode,
    )

    useEffect(() => {
        fetchClaim()
    }, [id])

    const close = () => {
        toggleVisible()
        onClose()
        setTimeout(() => history.goBack(), 300)
    }

    const onComplete = ({ data }, notif, ops) => {
        notif(`Patient has been discharged${status === 'Referral' ? ', Please fill the referral form' : ''}`, ops('success'))
        setPendingDischargeStatus('Discharged')
        getBedHistory()
        refetchAdmission()
        fetchClaim()
        onClose()

        if (status === 'Referral') {
            getAttachedDocs()
            setTab("3")
        }
    }

    const [{ loading: admLoading }, dischargePatient] = usePet('dischargePatient', { id, status }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Recording discharge...' } })

    const confirmDischarge = nextStatus => Confirm({
        title: 'Discharge patient?',
        message: 'Please confirm before performing this action',
        okText: 'Discharge patient',
        onOk: () => dischargePatient({ id, status: nextStatus })
    })

    const discharge = stat => {
        setStatus(stat)
        setPendingDischargeStatus(stat)

        if (!hasFinalConsent) {
            setShowDischargeOtp(true)
            return
        }

        confirmDischarge(stat)
    }

    const onEdit = () => {
        toggleIframe()
        setTimeout(toggleIframe, 100)
    }

    const onDischargeOtpSuccess = () => {
        setShowDischargeOtp(false)
        fetchClaim()
        confirmDischarge(pendingDischargeStatus || status || 'Discharged')
    }

    const cancelDischargeOtp = () => {
        setShowDischargeOtp(false)
        setPendingDischargeStatus('Discharged')
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            width="60%">
            <p className="font-extrabold text-3xl mb-0">Inpatient Record</p>
            <AdjustDates
                admission={ admission }
                toggle={ toggleAdjustDate }
                visible={ adjustDate }
                onPerform={ refetchAdmission } />
            <Receive
                progress
                loading={aLoading}
                error={aError}>
                {
                    admission ?
                        <>
                            {
                                move &&
                                <MovePatient
                                    admission={ admission }
                                    visible={ move }
                                    toggle={ toggleMove }
                                    onPerform={ getBedHistory } />
                            }
                            {
                                showDischargeOtp &&
                                <ConsentOtp
                                    visible={ showDischargeOtp }
                                    patient={ admission?.patient }
                                    admissionId={ admission?._id }
                                    mode="discharge"
                                    onSuccess={ onDischargeOtpSuccess }
                                    onCancel={ cancelDischargeOtp } />
                            }
                            {
                                claim?.status === 'rejected' &&
                                <div className="mb-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-red-700 mb-1">
                                                Claim rejected{claim?.rejectionTimestamp ? ` on ${stringifyDate(claim.rejectionTimestamp)}` : ''}
                                            </p>
                                            <p className="text-sm text-red-700 mb-0">
                                                {claim?.rejectionReason ?? 'No rejection reason was recorded.'}
                                            </p>
                                        </div>
                                        <Link to={`/app/accounts/sales/rejected-claims?claim=${claim?._id}`}>
                                            <button className="btn bg-white border border-red-200 text-red-700 hover:bg-red-100">
                                                View Worklist
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            }
                            <div className="flex items-center px-2 py-3">
                                <div className="w-28 h-28 flex items-center rounded-full justify-center overflow-hidden">
                                    <img className="h-24 w-24 mx-auto" src={images[`${admission?.patient?.gender?.toLowerCase()}_alt`] ?? images.male_alt} />
                                </div>
                                <div className="w-9/12 flex items-center">
                                    <div className="w-10/12 flex flex-col leading-none pl-4">
                                        <p className="text-2xl font-semibold mb-0">{admission?.patient?.name}</p>
                                        <p className="text-sm mb-0 font-light text-gray-700">
                                            {
                                                timeDiff(admission?.patient?.dob) < 0 ?
                                                    timeDiff(admission?.patient?.dob, 'months') < 0 ?
                                                        timeDiff(admission?.patient?.dob, 'weeks') < 0 ?
                                                            timeDiff(admission?.patient?.dob, 'days') < 0 ?
                                                                timeDiff(admission?.patient?.dob, 'hours')
                                                                : timeDiff(admission?.patient?.dob, 'days')
                                                            : timeDiff(admission?.patient?.dob, 'weeks')
                                                        : timeDiff(admission?.patient?.dob, 'months')
                                                    : timeDiff(admission?.patient?.dob)
                                            } Years Old (Born on {stringifyDate(admission?.patient?.dob, true)})
                                        </p>
                                        {admission?.served && <p className="text-gray-500 mb-0 text-xs italic">Admitted on {stringifyDate(admission?.servedOn)}</p>}
                                        {admission?.seen && <p className="text-gray-500 text-xs italic">Discharged on {stringifyDate(admission?.seenOn)}</p>}
                                    </div>
                                    <div className="w-2/12">
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    {
                                                        !admission?.seen && 
                                                        <>
                                                            <Menu.Item onClick={ () => discharge('Discharged') }>Discharge Patient</Menu.Item>
                                                            <Menu.Item onClick={ () => discharge('AMA Discharge') }>AMA Discharge</Menu.Item>
                                                            <Menu.Item onClick={ () => discharge('Referral') }>Referral Discharge</Menu.Item>
                                                            <Menu.Item onClick={ () => discharge('Deceased') }>Record Death</Menu.Item>
                                                            <Menu.Item onClick={ () => discharge('Absconded') }>Record Abscondment</Menu.Item>
                                                            <Menu.Item onClick={ toggleMove }>Move Patient</Menu.Item>
                                                        </>
                                                    }
                                                    <Menu.Item onClick={ toggleAdjustDate }>Adjust Dates</Menu.Item>
                                                    <Link to={`/app/inpatient/${path}/${id}/vitals`}>
                                                        <Menu.Item>Take Vitals</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/notes`}>
                                                        <Menu.Item>Take Notes</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/investigations`}>
                                                        <Menu.Item>Send for Investigations</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/treatment`}>
                                                        <Menu.Item>Request Treatment</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/medication`}>
                                                        <Menu.Item>Request Medication</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/items`}>
                                                        <Menu.Item>Request Items</Menu.Item>
                                                    </Link>
                                                    <Link to={`/app/inpatient/${path}/${id}/surgery`}>
                                                        <Menu.Item>Queue for Surgery</Menu.Item>
                                                    </Link>
                                                </Menu>
                                            }>
                                            <button className="btn ml-2 py-2.1 hisani-btn-green-light text-white">Actions <AiOutlineDown className="ml-2" /></button>
                                            {/* <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> </a> */}
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>
                            <Tabs onChange={ setTab } activeKey={ tab }>
                                <TabPane key="0" tab="Summary">
                                    <Receive
                                        error={bError}
                                        loading={bLoading}
                                        progress>
                                        <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2">
                                            <ClaimStatusCard admissionId={ admission?._id } />
                                            <PreauthStatusCard admissionId={ admission?._id } />
                                            {
                                                claim &&
                                                <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-4">
                                                    <div className="flex items-center justify-between border-b-2 border-gray-200 w-full pb-2">
                                                        <p className="font-medium text-sm mb-0">Claim Finalization</p>
                                                    </div>
                                                    <div className="pt-3">
                                                        <p className="text-sm text-gray-700 mb-2">
                                                            This admission is linked to a SHA claim. Final SHA consent must be confirmed before final discharge.
                                                        </p>
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            Service type: {claim?.serviceType} {claim?.authorizationCode ? `• Visit authorization: ${claim.authorizationCode}` : ''}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {claimConsentMethod === 'biometric'
                                                                ? `Final biometric authorization: ${
                                                                    claim?.dischargeAuthorizationCode
                                                                        ? 'captured'
                                                                        : 'not yet captured'
                                                                }`
                                                                : `Final OTP: ${
                                                                    claim?.dischargeOtp
                                                                        ? `captured${claim?.dischargeOtpCapturedAt ? ` on ${stringifyDate(claim.dischargeOtpCapturedAt)}` : ''}`
                                                                        : 'not yet captured'
                                                                }`}
                                                        </p>
                                                        <div className="flex justify-end">
                                                            <button
                                                                disabled={admLoading || cLoading}
                                                                onClick={() => !hasFinalConsent ? discharge(status || 'Discharged') : fetchClaim()}
                                                                className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                                                {!hasFinalConsent
                                                                    ? requiresDischargeBiometric
                                                                        ? 'Complete Biometric Discharge Authorization'
                                                                        : 'Complete Discharge OTP'
                                                                    : claimConsentMethod === 'biometric'
                                                                        ? 'Refresh Claim Finalization'
                                                                        : 'Refresh Claim Finalization'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                                                <div className="flex items-center justify-between border-b-2 border-gray-200 w-full pb-2">
                                                    <p className="font-medium text-sm mb-0">Inpatient History</p>
                                                </div>
                                                <ul className="list-none m-0 p-0 mt-3 pb-3">
                                                    {
                                                        beds.map(({ status, ward, bed, quantity, occupiedOn, vacatedOn }) =>
                                                            <li className="mb-2">
                                                                <div className="flex items-center mb-1">
                                                                    <div className={`${status === 'occupied' ? 'bg-yellow-500' : 'bg-blue-500'} rounded-full h-4 w-4 border-gray-200 border-2 z-10`}>
                                                                    </div>
                                                                    <div className="flex-1 ml-4 font-medium">{ bed } in { ward }</div>
                                                                </div>
                                                                <div className="ml-12">
                                                                    <p className="mb-0 text-xs">{ quantity } { pluralize('Day', quantity) }</p>
                                                                    <p className="text-gray-500 my-0 text-xs italic">Occupied on { stringifyDate(occupiedOn) }</p>
                                                                    { vacatedOn && <p className="text-gray-500 mt-0 text-xs italic">Vacated on { stringifyDate(vacatedOn) }</p> }
                                                                </div>
                                                            </li>
                                                        )
                                                    }
                                                </ul>           
                                            </div>     
                                        </div>
                                    </Receive>
                                </TabPane>
                                <TabPane key="1" tab="Timeline">
                                    <Timeline
                                        patient={admission?.patient}
                                        attachedTo={admission?._id}
                                        entryPathBase={`/app/inpatient/admissions/patients/${admission?.patient?._id}`} />
                                </TabPane>
                                <TabPane key="2" tab="Vitals">
                                    <Vitals path={ path } />
                                </TabPane>
                                <TabPane key="3" tab="Notes">
                                    <Notes path={ path } />
                                </TabPane>
                                <TabPane key="4" tab="Documents" >
                                    <Receive
                                        progress
                                        loading={attLoading}
                                        error={attError}>
                                        <Tabs defaultActiveKey="0" tabPosition="left">
                                            {
                                                attached?.map((att, i) =>
                                                    <TabPane key={i} tab={att.item.name} >
                                                        <div>
                                                            <div className="text-right">
                                                                <Link to={`/app/inpatient/${path}/${id}/${att.item?._id}`}>
                                                                    <button className="btn mb-2 py-2.1 hisani-btn-green-light text-white">Edit {att.item?.name} <AiOutlineEdit className="ml-2" /></button>
                                                                </Link>
                                                            </div>
                                                            {iframe && <iframe width="100%" height="768px" src={ `${server}/entry/${id}/${att.item?._id}/print`} />}
                                                        </div>
                                                    </TabPane>
                                                )
                                            }
                                        </Tabs>
                                    </Receive>
                                </TabPane>
                                <TabPane key="5" tab="Investigations" >
                                    <History
                                        group="Investigations"
                                        admission={admission} />
                                </TabPane>
                                <TabPane key="6" tab="Treatments">
                                    <History
                                        group="Treatment"
                                        admission={admission} />
                                </TabPane>
                                <TabPane key="7" tab="Medication">
                                    <MedHistory admission={admission} />
                                </TabPane>
                                <TabPane key="8" tab="Issued Items">
                                    <History
                                        name="issued items"
                                        group="Non-Medical Billed Items"
                                        admission={admission} />
                                </TabPane>
                                <TabPane key="9" tab="Surgery" >
                                    <History
                                        group="Surgery"
                                        name="surgeries"
                                        admission={admission} />
                                </TabPane>
                            </Tabs>
                        </>
                        : null
                }
            </Receive>
            <Switch>
                <Route path={`/app/inpatient/${path}/:id/investigations`}>
                    <SendFor
                        {...{ loading, error, groups, tab: "0" }}
                        department="InPatient"
                        source={`/app/inpatient/${path}/${id}`}
                        group="Investigations"
                        attachedTo={ admission?._id }
                        patient={admission?.patient} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/treatment`}>
                    <SendFor
                        {...{ loading, error, groups, tab: "0" }}
                        department="InPatient"
                        source={`/app/inpatient/${path}/${id}`}
                        group="Treatment"
                        attachedTo={ admission?._id }
                        patient={admission?.patient} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/medication`}>
                    <Medication
                        {...{ loading, error, groups, tab: "0" }}
                        department="InPatient"
                        source={`/app/inpatient/${path}/${id}`}
                        attachedTo={ admission?._id }
                        patient={admission?.patient} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/surgery`}>
                    <Surgery
                        {...{ loading, error, groups, tab: "0" }}
                        source={`/app/inpatient/${path}/${id}`}
                        attachedTo={ admission?._id }
                        patient={admission?.patient} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/items`}>
                    <RequestItems
                        {...{ loading, error, groups, tab: "0" }}
                        department="InPatient"
                        source={`/app/inpatient/${path}/${id}`}
                        attachedTo={ admission?._id }
                        patient={admission?.patient} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/patient`}>
                    <PatientHistory source={`/app/inpatient/${path}/${id}`} />
                </Route>
                <Route path={`/app/inpatient/${path}/:id/vitals`}>
                    <Receive
                        progress
                        loading={vLoading}
                        error={vError}>
                        { vitals ? <NotesDrawer attachedTo={ admission?._id } item={ vitals } onClose={onEdit} /> : null }
                    </Receive>
                </Route>
                <Route path={`/app/inpatient/${path}/:id/notes`}>
                    <Receive
                        progress
                        loading={nLoading}
                        error={nError}>
                        { notes ? <NotesDrawer attachedTo={ admission?._id } item={ notes } onClose={onEdit} /> : null }
                    </Receive>
                </Route>
                <Route path={`/app/inpatient/${path}/:id/:document`}>
                    <AdmDocument onClose={onEdit} />
                </Route>
            </Switch>
        </Drawer>
    )
}

export default Admission
